import { Button } from '@thanx/uikit/button'
import heroDemo from 'assets/images/recovery-ai/img_hero_demo.webp'
import heroDemoStill from 'assets/images/recovery-ai/img_hero_demo_still.webp'
import ExternalLink from 'components/Icons/ExternalLink'
import palette from 'constants/palette'
import usePrefersReducedMotion from 'hooks/usePrefersReducedMotion'
import { buildTranslate } from 'locales'
import React from 'react'
import { Translate } from 'react-redux-i18n'
import { StyleObject, useStyletron } from 'styletron-react'
import { feedbackProviderName } from '../feedbackOwnership'
import HeroIllustration from './HeroIllustration'
import type { OverviewContent } from './resolveOverviewState'

const t = buildTranslate('order_feedback')
const TRANSLATE_PREFIX = 'order_feedback'

// Measured from the pinned prototype. These five have no equivalent in
// constants/palette, so they are named here rather than silently rounded to a
// near-miss token — nephritis20 and the deprecated spearmint60 are both close
// to two of them without being equal. Promote them to palette if the design
// system adopts them.
const EYEBROW_TEAL = '#0e8c8a'
const LOCATION_BADGE_TEAL = '#13c1bf'
// Chip background behind the location badge — no equivalent token in
// constants/palette, same as the other four named above.
const LOCATION_BADGE_BACKGROUND = '#e3f7f7'
// Exported so HowItWorks's per-card image stage can reuse the exact same
// tint instead of redeclaring the literal in a second file.
export const HERO_PANEL_GRADIENT = 'linear-gradient(135deg, #eef4f7, #e4edf1)'
// The RecoveryAI states' panel tint — flat rather than the gradient above,
// and with no equivalent token in constants/palette, same as the five named
// at the top of this file.
const HERO_RAI_PANEL_TINT = '#f4f6f7'

// The repo's established stack/unstack boundary (three existing min-width:
// 768px uses in src/stylesheets/_utilities.scss) — reused here rather than
// picking a new one so the hero unstacks in step with the rest of the app's
// chrome (nav, tab bar) instead of at its own width.
export const HERO_STACK_BREAKPOINT = 768
const HERO_MEDIA_QUERY = `@media (min-width: ${HERO_STACK_BREAKPOINT}px)`

// Exported so the media-query branch can be asserted at the style-object
// level: styletron's css() is a no-op in jsdom (no class/rule reaches the
// DOM, and jsdom doesn't evaluate @media conditions), so the emitted rule
// can only be verified through the exported style builder, not the rendered
// output. Below the breakpoint the card is a single column, full width for
// the copy; at/above it, today's 52.5% / 47.5% split is unchanged.
export const HERO_CARD_GRID_STYLE: StyleObject = {
  gridTemplateColumns: '1fr',
  [HERO_MEDIA_QUERY]: {
    gridTemplateColumns: '52.5% 47.5%',
  },
}

// The illustration's tinted stage panel is hidden entirely below the
// breakpoint rather than shrunk — at 375px it's a gradient strip stealing
// half the width from copy that's already clipping, and it's decorative
// (aria-hidden) so hiding it loses nothing. HeroIllustration itself is
// untouched; only its container's visibility changes.
export const HERO_ILLUSTRATION_PANEL_STYLE: StyleObject = {
  display: 'none',
  [HERO_MEDIA_QUERY]: {
    display: 'block',
  },
}

// The RecoveryAI states' card. Stacks at the same breakpoint as the default
// one, but unstacks to the design's own `.rai-hero` split (0.92fr / 1.16fr,
// i.e. 44.23% / 55.77%), which widens the right panel, and carries that
// variant's card shadow. Exported for the same reason as the two objects
// above: the media-query branch is only assertable at the style-object level.
export const HERO_RAI_CARD_STYLE: StyleObject = {
  gridTemplateColumns: '1fr',
  boxShadow: 'rgba(30, 38, 48, 0.07) 0px 1px 3px 0px',
  [HERO_MEDIA_QUERY]: {
    gridTemplateColumns: '0.92fr 1.16fr',
  },
}

// The RecoveryAI states' right-hand panel, used instead of
// HERO_ILLUSTRATION_PANEL_STYLE there. The default states inherit their
// panel's gradient, padding and centring from `.hero-r` inside
// HeroIllustration's copied markup; this variant renders no illustration, so
// the design's `.rai-hero .hero-r` values have to be declared here instead.
export const HERO_RAI_PANEL_STYLE: StyleObject = {
  display: 'none',
  backgroundColor: HERO_RAI_PANEL_TINT,
  padding: '8px',
  [HERO_MEDIA_QUERY]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}

// The product shot that fills the RecoveryAI panel, matching the design's own
// `.rai-hero-gif` rule. `max-width` caps it above the panel's own width so the
// shot stops growing before it outruns its 760px source.
const HERO_RAI_IMAGE_STYLE: StyleObject = {
  width: '100%',
  maxWidth: '720px',
  height: 'auto',
  display: 'block',
  borderRadius: '12px',
}

// The comments-off banner's icon chip, and the scale its two sub-lines share.
// Both are the design's `.comments-off` values; the sub-lines are a step down
// from the bolded first line rather than the same 16px.
const BANNER_ICON_CHIP_SIZE = '42px'
const BANNER_SUBLINE_STYLE: StyleObject = {
  fontSize: '13.5px',
  lineHeight: '20.25px',
  color: palette.grey70,
  // The margin is one-sided by design. Stated in full rather than as
  // marginTop so the app's global <p> bottom margin doesn't survive and add
  // roughly 10px per line on top of it.
  margin: '3px 0 0',
}

// The aggregate ratings report — a route of this app, not this page. The two
// are a standing mixup (/order_feedback is this page), so the destination is
// named rather than inlined mid-sentence. Relative on purpose: see the link
// below for why the origin must not be hard-coded.
const ORDER_RATINGS_PATH = '/order_ratings'

// The app's in-prose link treatment: the colour carries it at rest (as in
// OrderingManagement's ErrorCategoryCard), with the Overview's own hover
// underline. Everything else is inherited from the line it sits in.
const BANNER_LINK_STYLE: StyleObject = {
  color: palette.belizeHole,
  textDecoration: 'none',
  // Keeps the trailing ExternalLink glyph on the same line as the label and
  // riding the text baseline, so the pair wraps as one unit.
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  ':hover': { textDecoration: 'underline' },
}

// The banner's decorative glyph, copied from the design's `.co-lock` — no
// icon in components/Icons carries this receipt shape. Promote it there if a
// second caller ever needs it.
const CommentsOffIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M8 2V6M12 2V6M16 2V6M16 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V8M20 12V14M20 18V20C20 20.5304 19.7893 21.0391 19.4142 21.4142C19.0391 21.7893 18.5304 22 18 22H17M13 22H11M7 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V18M4 14V12M4 8V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H8M8 10H14M8 14H16M8 18H13" />
  </svg>
)

type LocationBadgeValues = {
  enabled: number
  total: number
}

export type HeroCta = {
  key: string
  onClick: () => void
}

type Props = {
  content: OverviewContent
  ctas: HeroCta[]
  // Not one of the resolver's three signals: the resolver decides only
  // whether the badge renders, never the numbers in it. Supplied once a real
  // per-location enabled count exists.
  locationBadgeValues?: LocationBadgeValues
}

// Reusable hero region for every Overview state: an optional status banner,
// then the marketing card — a two-column panel whose right half is a tinted
// stage for the product mockup. Consumes resolveOverviewState's descriptor
// only; it never decides copy or visibility itself.
//
// context:
//   The card is one grid rather than two floated halves so the tinted panel
//   always runs the full height of the copy beside it, however long that copy
//   gets. overflow:hidden is what keeps the tint inside the rounded corner.
const Hero = ({ content, ctas, locationBadgeValues }: Props) => {
  const [css] = useStyletron()
  const prefersReducedMotion = usePrefersReducedMotion()
  const { hero, thirdPartyProvider, showCommentsOffBanner } = content

  // Only the opening clause of the banner's second line names a tool; every
  // other piece of the banner renders when this is undefined.
  const providerName = feedbackProviderName(thirdPartyProvider)

  return (
    <>
      {showCommentsOffBanner && (
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '18px 22px',
            margin: '24px 0 22px',
            backgroundColor: palette.white,
            border: `1px solid ${palette.grey20}`,
            borderRadius: '12px',
            color: palette.grey90,
          })}
        >
          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              width: BANNER_ICON_CHIP_SIZE,
              height: BANNER_ICON_CHIP_SIZE,
              borderRadius: '10px',
              backgroundColor: palette.grey10,
              color: palette.grey70,
            })}
          >
            <CommentsOffIcon />
          </div>

          {/* min-width:0 so a long line wraps inside this column rather than
              pushing it past its flex basis and squeezing the chip. */}
          <div className={css({ flex: '1 1 0%', minWidth: 0 })}>
            {/* Bold in full, unlike lines 2 and 3 — a whole-string emphasis
                that needs no rich-text interpolation. */}
            <strong
              className={css({
                display: 'block',
                fontSize: '16px',
                lineHeight: '24px',
              })}
            >
              {t('comments_off_banner.line_1')}
            </strong>
            {/* Always rendered. Only the opening clause names the connected
                tool, so that clause alone is dropped when there isn't one —
                the ratings report is reachable either way, so the sentence
                that points at it must not disappear with the provider. */}
            <p className={css(BANNER_SUBLINE_STYLE)}>
              {providerName && (
                <>
                  {t('comments_off_banner.line_2_provider', {
                    provider: providerName,
                  })}{' '}
                </>
              )}
              {t('comments_off_banner.line_2')}{' '}
              {/* Opens in a new tab, per the design: the banner sits at the
                  top of a page the merchant is still reading, so sending them
                  away mid-sentence would lose their place. A plain <a> rather
                  than a router Link because a router link cannot target a new
                  tab; the href stays relative so it resolves against whichever
                  origin the app is served from (the report is a route of THIS
                  app — note that REACT_APP_MERCHANT_ROOT_URL is the LEGACY
                  Rails dashboard host, so it is the wrong base for it).

                  The ↗ is an icon, not a character in the copy, and is
                  aria-hidden; the aria-label carries the new-window
                  announcement for screen readers, matching PromoCodes'
                  CompatibilityCard. */}
              <a
                href={ORDER_RATINGS_PATH}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${t('comments_off_banner.line_2_link')} ${t(
                  'opens_in_new_window'
                )}`}
                className={css(BANNER_LINK_STYLE)}
              >
                {t('comments_off_banner.line_2_link')}
                <ExternalLink color={palette.belizeHole} />
              </a>
            </p>
            <p className={css(BANNER_SUBLINE_STYLE)}>
              {t('comments_off_banner.line_3')}
            </p>
          </div>
        </div>
      )}

      {hero.visible && (
        <div
          className={css({
            display: 'grid',
            ...(hero.useRaiVariant
              ? HERO_RAI_CARD_STYLE
              : HERO_CARD_GRID_STYLE),
            // The design's `.ov-hero` margin. Collapses against the banner's
            // 22px bottom margin when the banner is present, so the gap is
            // 28px either way — same as the prototype.
            marginTop: '28px',
            backgroundColor: palette.white,
            border: `1px solid ${palette.grey20}`,
            borderRadius: '12px',
            overflow: 'hidden',
          })}
        >
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: '44px',
            })}
          >
            <div
              className={css({
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '1.44px',
                textTransform: 'uppercase',
                color: EYEBROW_TEAL,
                marginBottom: '14px',
              })}
            >
              {t('eyebrow')}
            </div>

            {hero.showLocationBadge && hero.badgeKey && locationBadgeValues && (
              <div
                className={css({
                  display: 'flex',
                  // Hugs its own text rather than stretching to the hero
                  // column's width, which a flex-column parent's default
                  // stretch would otherwise do.
                  alignSelf: 'flex-start',
                  backgroundColor: LOCATION_BADGE_BACKGROUND,
                  color: LOCATION_BADGE_TEAL,
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: 700,
                  // Written out rather than inherited: the design's global
                  // line-height is 1.5, this app's is 1.4286, so an
                  // undeclared value renders the pill 2.3px taller than
                  // designed.
                  lineHeight: '12px',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  marginBottom: '14px',
                })}
              >
                {t(hero.badgeKey, {
                  enabled: locationBadgeValues.enabled,
                  total: locationBadgeValues.total,
                })}
              </div>
            )}

            {hero.headlineKey && (
              <h2
                className={css({
                  fontSize: '30px',
                  fontWeight: 900,
                  lineHeight: '34.8px',
                  color: palette.grey90,
                  margin: '0 0 12px',
                })}
              >
                {t(hero.headlineKey)}
              </h2>
            )}

            {hero.ledeKey && (
              // dangerousHTML so a state's lede copy can carry inline
              // emphasis (e.g. `rai_thanx`/`rai_all` bolding "Feedback")
              // without splitting the sentence in this component — the
              // markup lives in the locale string, not here.
              <Translate
                tag="p"
                className={css({
                  fontSize: '16.5px',
                  lineHeight: '24.75px',
                  color: palette.grey70,
                  // The design's own `.lede` measure. Left in ch rather than
                  // resolved to the 459.36px it computes to in Lato, so it
                  // stays a measure if the type scale ever moves.
                  maxWidth: '48ch',
                  margin: '0 0 22px',
                })}
                value={`${TRANSLATE_PREFIX}.${hero.ledeKey}`}
                provider={providerName}
                dangerousHTML
              />
            )}

            {hero.nudgeKey && locationBadgeValues && (
              // The design gives this paragraph the same `.lede` styling as
              // the one above it — in the prototype it is literally a second
              // `.lede`, not a class of its own — so it carries the same
              // measure, in the same `ch` unit, rather than a pixel literal
              // that would drift from the lede's the next time either moves.
              <p
                className={css({
                  fontSize: '16.5px',
                  lineHeight: '24.75px',
                  color: palette.grey70,
                  maxWidth: '48ch',
                  margin: '0 0 22px',
                })}
              >
                {t(hero.nudgeKey, {
                  // The remainder, not the enabled count again — see
                  // resolveOverviewState's comment on this exact mixup.
                  remaining:
                    locationBadgeValues.total - locationBadgeValues.enabled,
                })}
              </p>
            )}

            {ctas.length > 0 && (
              <div
                className={css({
                  display: 'flex',
                  gap: '12px',
                  // Two buttons side by side in a narrow single-column
                  // layout (see HERO_CARD_GRID_STYLE) would otherwise
                  // overflow the copy column rather than stack.
                  flexWrap: 'wrap',
                })}
              >
                {ctas.map(({ key, onClick }, index) => (
                  <Button
                    key={key}
                    kind={index === 0 ? 'primary' : 'secondary'}
                    onClick={onClick}
                  >
                    {t(key)}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Visibility-only wrapper in the default states: the tinted panel
              itself is `.hero-r` inside HeroIllustration, copied from the
              prototype along with its gradient, padding and centring, so
              duplicating any of that here would fight the copied stylesheet.
              The RecoveryAI states run no hand-built animation — the design
              fills that half with a recording of the product instead of the
              phone stage — so they bring their own panel styling. */}
          <div
            className={css(
              hero.useRaiVariant
                ? HERO_RAI_PANEL_STYLE
                : HERO_ILLUSTRATION_PANEL_STYLE
            )}
          >
            {hero.useRaiVariant ? (
              // The same RecoveryAI demo recording the promo landing hero
              // already ships, transcoded to animated WebP: identical 760x570
              // frames and 16.75s loop at a quarter of the GIF's bytes. It
              // loops forever and nothing can pause an animated image, so a
              // reduced-motion viewer gets the recording's own end state as a
              // still — the same substitution the hand-built hero animation
              // makes for that viewer.
              <img
                src={prefersReducedMotion ? heroDemoStill : heroDemo}
                alt={t('rai_hero_alt')}
                // The panel is display:none below the breakpoint, yet the
                // image is still fetched there — half a megabyte a narrow
                // viewport never shows. This asks the browser to defer it;
                // whether it actually skips a display:none image is up to the
                // browser, and measuring it needs a foreground tab, so treat
                // this as a hint rather than a guarantee.
                loading="lazy"
                className={css(HERO_RAI_IMAGE_STYLE)}
              />
            ) : (
              <HeroIllustration />
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default Hero
